import { z } from "zod";
import { APPOINTMENT_STATUS_OF_ROW, optional, type AppointmentRowStatus } from "./appointments";
import { ADMIN_PASSWORD_MAX_LENGTH, personPhone } from "./admin-people";
import { membershipStatus, recordId } from "./contracts";
import { absentOrBlankAsNull, searchTextUpTo, unset } from "./fields";
import { emailAddress, settingsName } from "./organization-settings";
import { settableWorkerContractStatus, WORKER_CONTRACT_NOTES_MAX, type SettableWorkerContractStatus } from "./worker-contracts";

export const ADMIN_DETAIL_PAGE_SIZE_MAX = 100;

const SEARCH_MAX_LENGTH = 200;

const count = z.number().int().nonnegative();

const pageOf = <T extends z.ZodTypeAny>(item: T) => z.object({ data: z.array(item), total: count, page: count, pageSize: count, totalPages: count });

export const adminDetailPageQuery = z.object({
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(ADMIN_DETAIL_PAGE_SIZE_MAX).default(20)),
});
export type AdminDetailPageQuery = z.infer<typeof adminDetailPageQuery>;

export const adminWorkerContractListQuery = adminDetailPageQuery.extend({
  search: optional(searchTextUpTo(SEARCH_MAX_LENGTH)),
  status: optional(membershipStatus),
});
export type AdminWorkerContractListQuery = z.infer<typeof adminWorkerContractListQuery>;

export const adminWorkerContract = z.object({
  id: recordId,
  organization: recordId,
  user: z.object({ id: recordId, name: z.string(), email: z.string(), image: z.string().optional() }),
  status: membershipStatus,
  notes: z.string().optional(),
  services: z.array(recordId),
  isCompanyMember: z.boolean(),
  createdAt: z.string(),
});
export type AdminWorkerContract = z.infer<typeof adminWorkerContract>;

export const adminWorkerContractPage = pageOf(adminWorkerContract);
export type AdminWorkerContractPage = z.infer<typeof adminWorkerContractPage>;

const notes = z.preprocess(absentOrBlankAsNull, z.string().max(WORKER_CONTRACT_NOTES_MAX).nullable());

export const adminWorkerContractAddRequest = z
  .object({
    name: settingsName,
    email: emailAddress,
    password: optional(z.string().max(ADMIN_PASSWORD_MAX_LENGTH)),
    phone: personPhone,
    notes,
  })
  .strict();
export type AdminWorkerContractAddRequest = { name: string; email: string; password?: string; phone?: string | null; notes?: string | null };

export const adminWorkerContractUpdateRequest = z.object({ status: settableWorkerContractStatus, notes }).strict();
export type AdminWorkerContractUpdateRequest = { status: SettableWorkerContractStatus; notes: string | null };

export const adminSubcontractorPeopleQuery = adminDetailPageQuery.extend({ search: optional(searchTextUpTo(SEARCH_MAX_LENGTH)) });
export type AdminSubcontractorPeopleQuery = z.infer<typeof adminSubcontractorPeopleQuery>;

const APPOINTMENT_ROW_STATUSES = Object.keys(APPOINTMENT_STATUS_OF_ROW) as [AppointmentRowStatus, ...AppointmentRowStatus[]];

export const adminSubcontractorJobQuery = adminDetailPageQuery.extend({ status: optional(z.enum(APPOINTMENT_ROW_STATUSES)) });
export type AdminSubcontractorJobQuery = z.infer<typeof adminSubcontractorJobQuery>;

export const adminSubcontractorJob = z.object({
  id: recordId,
  date: z.string(),
  time: z.string().nullable(),
  status: z.string(),
  finalPrice: z.number().nullable(),
  subsPayment: z.number().nullable(),
  subsPaymentStatus: z.string().nullable(),
  propertyName: z.string(),
  clientName: z.string(),
  assignedUserName: z.string().nullable(),
});
export type AdminSubcontractorJob = z.infer<typeof adminSubcontractorJob>;

export const adminSubcontractorJobPage = pageOf(adminSubcontractorJob);
export type AdminSubcontractorJobPage = z.infer<typeof adminSubcontractorJobPage>;

export const adminSubcontractorPayout = z.object({
  id: recordId,
  amount: z.number(),
  currency: z.string(),
  status: z.string(),
  createdAt: z.string(),
  stripeTransferId: z.string().nullable(),
});
export type AdminSubcontractorPayout = z.infer<typeof adminSubcontractorPayout>;

export const adminSubcontractorPayoutPage = pageOf(adminSubcontractorPayout);
export type AdminSubcontractorPayoutPage = z.infer<typeof adminSubcontractorPayoutPage>;

export const adminSubcontractorAutopayQuery = z.object({ vendor: recordId.toLowerCase() });
export type AdminSubcontractorAutopayQuery = z.infer<typeof adminSubcontractorAutopayQuery>;

export const adminSubcontractorAutopay = z.object({
  enabled: z.boolean(),
  pausedAt: z.string().nullable(),
  pauseReason: z.string().nullable(),
  consecutiveFailures: z.number().int(),
  lastRunAt: z.string().nullable(),
  lastRunOutcome: z.string().nullable(),
  lastRunError: z.string().nullable(),
  lastRunAmountCents: z.number().int().nullable(),
});
export type AdminSubcontractorAutopay = z.infer<typeof adminSubcontractorAutopay>;

export const adminSubcontractorAutopayAnswer = adminSubcontractorAutopay.nullable();

export const adminSubcontractorDocument = z.object({
  id: recordId,
  filename: z.string(),
  uploadedAt: z.string(),
  expiresOn: z.string().nullable(),
  uploadedByName: z.string().nullable(),
  downloadUrl: z.string().url().nullable(),
});
export type AdminSubcontractorDocument = z.infer<typeof adminSubcontractorDocument>;

export const adminSubcontractorDocumentList = z.array(adminSubcontractorDocument);
